import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { assets, offer, whatsappPrefill } from "@/config/site";
import { EASE, Magnetic } from "./Reveal";
import { RMark } from "./RMark";

export const Hero = ({ started }) => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

    const line = (i) => ({
        initial: { y: reduce ? 0 : "110%" },
        animate: started ? { y: "0%" } : { y: reduce ? 0 : "110%" },
        transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE },
    });

    const fade = (delay) => ({
        initial: { opacity: 0, y: reduce ? 0 : 16 },
        animate: started ? { opacity: 1, y: 0 } : {},
        transition: { duration: 0.7, delay, ease: EASE },
    });

    return (
        <section
            id="top"
            ref={ref}
            aria-labelledby="hero-title"
            className="relative flex min-h-[100svh] flex-col overflow-hidden"
        >
            <motion.div style={reduce ? undefined : { y: imgY }} className="rz-scan rz-scan-ambient absolute inset-0">
                <motion.img
                    src={assets.facility}
                    alt="The Revautoz studio in Colvale, Mapusa, Goa — Ride. Redefined. signage above the entrance"
                    className="h-full w-full object-cover object-[50%_42%]"
                    fetchPriority="high"
                    decoding="async"
                    initial={{ scale: reduce ? 1 : 1.15 }}
                    animate={started ? { scale: 1 } : {}}
                    transition={{ duration: 2.4, ease: EASE }}
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-rz-black via-rz-black/60 to-rz-black/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-rz-black/70 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-rz-black/45" />

            <div className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-start px-5 pb-16 pt-24 sm:px-8 lg:px-12">
                <motion.p
                    {...fade(0.1)}
                    className="font-display text-[11px] uppercase tracking-[0.25em] text-rz-smoke"
                >
                    Revautoz <span className="text-rz-red">—</span> by Apex Automotive
                    <span className="mx-3 text-rz-steel">|</span> Goa
                </motion.p>

                <motion.div {...fade(0.25)} className="mt-8 flex items-center gap-3">
                    <RMark className="h-6 w-6 text-rz-white" />
                    <span className="font-display text-[11px] uppercase tracking-[0.3em] text-rz-smoke">
                        {offer.label}
                    </span>
                </motion.div>

                <h1
                    id="hero-title"
                    className="mt-5 font-display font-bold uppercase leading-[0.95] tracking-tight"
                >
                    <span className="block overflow-hidden">
                        <motion.span {...line(0)} className="block text-3xl text-rz-white sm:text-5xl lg:text-6xl">
                            Up to
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden">
                        <motion.span
                            {...line(1)}
                            className="block text-[22vw] text-rz-red sm:text-[11rem] lg:text-[13rem]"
                        >
                            20% off
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden">
                        <motion.span {...line(2)} className="block text-3xl text-rz-white sm:text-5xl lg:text-6xl">
                            Select services
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    {...fade(0.6)}
                    className="mt-6 font-display text-base font-semibold uppercase tracking-[0.2em] text-rz-white sm:text-lg"
                >
                    <span className="text-rz-red">+</span> Free car inspection report
                </motion.p>

                <motion.p {...fade(0.7)} className="mt-4 max-w-xl text-base text-rz-smoke sm:text-lg">
                    Find out exactly what your vehicle needs before you spend.
                </motion.p>

                <motion.div {...fade(0.8)} className="mt-9 flex flex-wrap items-center gap-4">
                    <Magnetic>
                        <a
                            href={whatsappPrefill(offer.claimMessage)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-offer-cta"
                            className="btn-sweep block bg-rz-red px-8 py-4 font-display text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-rz-reddeep"
                        >
                            Claim My Offer
                        </a>
                    </Magnetic>
                    <a
                        href={whatsappPrefill(offer.message)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-inspection-cta"
                        className="border border-rz-white/40 px-8 py-4 font-display text-[13px] font-semibold uppercase tracking-[0.18em] text-rz-white transition-colors duration-300 hover:border-rz-white hover:bg-rz-white/5"
                    >
                        Get My Free Inspection
                    </a>
                </motion.div>

                <motion.div {...fade(0.95)} className="mt-10 border-t border-rz-steel/60 pt-5">
                    <p className="font-display text-[10px] uppercase tracking-[0.3em] text-rz-smoke">
                        Precision · Protection · Performance · Craftsmanship
                    </p>
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-rz-smoke">
                        <Star size={11} className="shrink-0 fill-rz-red text-rz-red" />
                        (4.9 rated on Google Business — Best Detailing Studio in Goa · @revautoz on
                        Instagram)
                    </p>
                </motion.div>
            </div>
        </section>
    );
};
